import { useCallback } from 'react'
import { ONBOARDING_STEPS } from '@/constants/onboarding'
import { useUserIdentified } from '@/hooks/utils/useUserIdentified'

export const useOnboardingRedirect = () => {
  const { isLoggedIn, profile, isIdentified } = useUserIdentified()

  const getNextOnboardingRoute = useCallback((): string | null => {
    // 1. If the identity hook is still loading, return null ("waiting for data")
    if (!isIdentified) {
      return null
    }

    // 2. If it finished loading but there is no active session, the destination is Home
    if (!isLoggedIn || !profile) {
      return '/'
    }

    // Merely referencing `sessionStorage` can throw a SecurityError (not just
    // the individual get/set/remove calls) in some embedding contexts —
    // strict storage partitioning, Safari private browsing, or a cross-origin
    // iframe with storage access denied. `typeof window !== 'undefined'`
    // guards nothing here since `window` itself is defined; only a try/catch
    // around the property access protects us. See FZ-WEB-NEXTJS-P1.
    try {
      const isNewUser = sessionStorage.getItem('is_new_user') === 'true'
      const authProvider = sessionStorage.getItem('auth_provider')

      const context = {
        profile: profile as any,
        isNewUser,
        authProvider,
        sessionStorage,
        localStorage: typeof window !== 'undefined' ? window.localStorage : ({} as Storage),
      }

      // 3. We look for the corresponding step
      const pendingStep = ONBOARDING_STEPS.find(step => step.shouldShow(context))

      if (pendingStep) {
        if (pendingStep.name === 'welcome') {
          sessionStorage.removeItem('is_new_user')
          sessionStorage.setItem('welcome_shown_session', 'true')
        }
        return pendingStep.route
      }

      // 4. If the profile loaded correctly and has no pending steps, it goes to '/'
      return '/'
    } catch {
      // Storage inaccessible: skip onboarding steps rather than crash, same
      // fallback as the "no active session" branch above.
      return '/'
    }
  }, [isIdentified, isLoggedIn, profile])

  return { getNextOnboardingRoute, isIdentified, profile }
}